/**
 * SFrame161 route — Business interests & valuation table (Stream E).
 * Persists with personal/digital data under the same `sframe-160` frame key.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { SettlementStepPage } from '../../settlement';
import Modal from '../../ui/Modal';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession, type SettlementFrameData } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  migrateSFrame160Data,
  type ValuationTableRow,
  type BusinessInterestRow,
  type SFrame160Persisted,
} from './sframe160Persisted';

const step = SETTLEMENT_PHASE_CONFIGS['secure-inventory'].steps.find((s) => s.id === 'si-5')!;

const nid = () => `n-${Math.random().toString(36).slice(2, 9)}`;

const VAL_STATUSES: ValuationTableRow['status'][] = [
  'Needs Valuation',
  'Professional Engaged',
  'In Progress',
  'Completed',
];

const VALUATION_TYPES_NO_EMPTY: Exclude<ValuationTableRow['valuationType'], ''>[] = ['CPA', 'Appraiser', 'Self'];

interface Real155Row {
  id: string;
  address: string;
}

interface Vehicle155Row {
  id: string;
  description: string;
}

function parseMoney(s: string): number {
  const n = Number(String(s).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function asArr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function defaultValuationRow(
  partial: Omit<ValuationTableRow, keyof ValuationTableRow> & Partial<ValuationTableRow>
): ValuationTableRow {
  return {
    id: partial.id ?? nid(),
    assetRef: partial.assetRef ?? nid(),
    assetType: partial.assetType ?? 'Asset',
    name: partial.name ?? '',
    valuationType: partial.valuationType ?? '',
    professional: partial.professional ?? '',
    status: partial.status ?? 'Needs Valuation',
    dodValue: partial.dodValue ?? '',
    reportFileName: partial.reportFileName ?? '',
    reportFileSize: partial.reportFileSize ?? 0,
    dodConfirmed: partial.dodConfirmed ?? false,
    complete: partial.complete ?? false,
  };
}

async function buildSeeds155(
  estateId: string | undefined,
  loadFrameData: (id: string) => Promise<SettlementFrameData | null>
): Promise<ValuationTableRow[]> {
  if (!estateId) return [];
  const fd = await loadFrameData('sframe-155');
  const d = fd?.data as { realEstate?: Real155Row[]; vehicles?: Vehicle155Row[] } | undefined;
  const out: ValuationTableRow[] = [];
  for (const r of d?.realEstate ?? []) {
    const nm = String(r.address || '').trim();
    if (!nm) continue;
    out.push(
      defaultValuationRow({
        assetRef: `155-real-${r.id}`,
        assetType: 'Real estate',
        name: nm,
      })
    );
  }
  for (const v of d?.vehicles ?? []) {
    const nm = String(v.description || '').trim();
    if (!nm) continue;
    out.push(
      defaultValuationRow({
        assetRef: `155-veh-${v.id}`,
        assetType: 'Vehicle',
        name: nm,
      })
    );
  }
  return out;
}

async function buildSeedsPlannerAccounts(
  estateId: string | undefined,
  loadPlannerData: (id: string) => Promise<Record<string, unknown> | null>
): Promise<ValuationTableRow[]> {
  if (!estateId) return [];
  const frames: Array<[string, string]> = [
    ['frame-186', 'cashAccounts'],
    ['frame-187', 'investmentAccounts'],
    ['frame-188', 'retirementAccounts'],
    ['frame-189', 'otherFinancialAssets'],
  ];
  const out: ValuationTableRow[] = [];
  let offset = 0;
  for (const [fid, key] of frames) {
    const data = await loadPlannerData(fid);
    const rows = asArr<Record<string, unknown>>(data?.[key]);
    rows.forEach((row, idx) => {
      const title = String(row.name || row.policyName || 'Account').trim() || 'Account';
      const inst = String(row.institution || row.payerInstitution || row.type || '').trim();
      const name = inst ? `${title} — ${inst}` : title;
      out.push(
        defaultValuationRow({
          assetRef: `planner-${fid}-${idx}-${offset}`,
          assetType: `Financial (${fid.replace('frame-', 'F')})`,
          name,
        })
      );
      offset += 1;
    });
  }
  return out;
}

function mergeSeedValuations(existing: ValuationTableRow[], seeds: ValuationTableRow[]): ValuationTableRow[] {
  const byRef = new Set(existing.map((r) => r.assetRef));
  const next = [...existing];
  for (const s of seeds) {
    if (!byRef.has(s.assetRef)) {
      next.push(s);
      byRef.add(s.assetRef);
    }
  }
  return next;
}

function reconcileBusinessValuations(
  biz: BusinessInterestRow[],
  valuations: ValuationTableRow[]
): ValuationTableRow[] {
  const validBizRef = new Set(biz.map((b) => `biz-${b.id}`));
  const cleaned = valuations.filter((v) => !v.assetRef.startsWith('biz-') || validBizRef.has(v.assetRef));
  const byRef = new Map(cleaned.map((v) => [v.assetRef, { ...v }]));

  for (const b of biz) {
    const ref = `biz-${b.id}`;
    const name = b.entityName.trim() || 'Business interest';
    const cur = byRef.get(ref);
    if (cur) byRef.set(ref, { ...cur, name });
    else
      byRef.set(
        ref,
        defaultValuationRow({
          assetRef: ref,
          assetType: 'Business interest',
          name,
        })
      );
  }

  const seen = new Set<string>();
  const out: ValuationTableRow[] = [];
  for (const v of cleaned) {
    const m = byRef.get(v.assetRef)!;
    out.push(m);
    seen.add(v.assetRef);
  }
  for (const b of biz) {
    const ref = `biz-${b.id}`;
    if (!seen.has(ref)) {
      out.push(byRef.get(ref)!);
      seen.add(ref);
    }
  }
  return out;
}

function valuationBadges(row: ValuationTableRow) {
  const pills: { key: string; className: string; label: string }[] = [];
  if (row.status === 'Needs Valuation') pills.push({ key: 'nv', className: 'bg-amber-100 text-amber-900', label: 'Needs Valuation' });
  else if (row.status === 'Professional Engaged')
    pills.push({ key: 'pe', className: 'bg-blue-100 text-blue-900', label: 'Professional Engaged' });
  else if (row.status === 'In Progress') pills.push({ key: 'ip', className: 'bg-slate-100 text-slate-800', label: 'In Progress' });
  else if (row.status === 'Completed')
    pills.push({ key: 'vc', className: 'bg-emerald-100 text-emerald-900', label: 'Valuation Complete' });
  if (row.dodConfirmed) pills.push({ key: 'dod', className: 'bg-teal-100 text-teal-900', label: 'DOD Value Confirmed' });
  return pills;
}

export default function SFrame160Business() {
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, loadPlannerData } = useSettlementSession(estateId ?? null, {
    readOnly: isArchived,
  });

  const persistedRef = useRef<SFrame160Persisted>(migrateSFrame160Data(undefined));

  const [valuationTable, setValuationTable] = useState<ValuationTableRow[]>([]);
  const [businessInterests, setBusinessInterests] = useState<BusinessInterestRow[]>([]);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(!estateId);
  const seedsDoneRef = useRef(false);

  const [bizModalOpen, setBizModalOpen] = useState(false);
  const [editingBiz, setEditingBiz] = useState<BusinessInterestRow | null>(null);
  const [personalValueSum, setPersonalValueSum] = useState(0);
  const [digitalAssetCount, setDigitalAssetCount] = useState(0);

  useEffect(() => {
    seedsDoneRef.current = false;
  }, [estateId]);

  useEffect(() => {
    if (!estateId) {
      setHydrated(true);
      return;
    }
    let cancelled = false;
    const run = async () => {
      const fd = await loadFrameData('sframe-160');
      if (cancelled) return;
      const m = migrateSFrame160Data(fd?.data as Record<string, unknown> | undefined);
      persistedRef.current = m;
      setPersonalValueSum(m.personalItems.reduce((s, p) => s + parseMoney(p.value), 0));
      setDigitalAssetCount(m.digitalAssets.length);
      const seeds155 = await buildSeeds155(estateId, loadFrameData);
      const seedAccounts = await buildSeedsPlannerAccounts(estateId, loadPlannerData);
      if (cancelled) return;

      let vt = [...m.valuationTable];
      if (!seedsDoneRef.current) {
        vt = mergeSeedValuations(vt, [...seeds155, ...seedAccounts]);
        seedsDoneRef.current = true;
      }
      vt = reconcileBusinessValuations(m.businessInterests, vt);

      setValuationTable(vt);
      setBusinessInterests(m.businessInterests.length ? m.businessInterests : []);
      setHydrated(true);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData, loadPlannerData]);

  useEffect(() => {
    setValuationTable((vt) => reconcileBusinessValuations(businessInterests, vt));
  }, [businessInterests]);

  useEffect(() => {
    if (!estateId || !hydrated || isArchived) return;
    const t = window.setTimeout(() => {
      const base = persistedRef.current;
      persistedRef.current = {
        ...base,
        valuationTable,
        businessInterests,
      };
      void saveFrameData('sframe-160', {
        ...base,
        valuationTable,
        businessInterests,
      });
    }, 600);
    return () => window.clearTimeout(t);
  }, [valuationTable, businessInterests, estateId, hydrated, saveFrameData, isArchived]);

  const patchRow = useCallback((id: string, patch: Partial<ValuationTableRow>) => {
    setValuationTable((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const emptyBiz = (): BusinessInterestRow => ({
    id: nid(),
    entityName: '',
    ownershipPct: '',
    role: '',
    advisorContact: '',
    notes: '',
  });

  const saveBizModal = () => {
    if (!editingBiz) return;
    setBusinessInterests((rows) => {
      const exists = rows.some((r) => r.id === editingBiz.id);
      if (exists) return rows.map((r) => (r.id === editingBiz.id ? editingBiz : r));
      return [...rows, editingBiz];
    });
    setBizModalOpen(false);
    setEditingBiz(null);
  };

  const totalAssets = valuationTable.length;
  const completedCount = valuationTable.filter((r) => r.complete).length;
  const portfolioTotal = personalValueSum + valuationTable.reduce((s, v) => s + parseMoney(v.dodValue), 0);

  const headerNote = digitalAssetCount > 0 ? `Digital assets tracked: ${digitalAssetCount}` : null;

  return (
    <SettlementStepPage
      step={step}
      phase="secure-inventory"
      overviewRoute="/sframe-143"
      hidePlannerBanner
      settlementDocumentContext={{ phase: 'secure-inventory', stepId: step.id }}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        {estateId ? (
          <Link to={`/sframe-160/${estateId}`} className="font-semibold text-cyan-700 hover:underline">
            ← Personal & digital property
          </Link>
        ) : (
          <span className="text-stone-500">Select an estate to continue.</span>
        )}
      </div>

      <div className="mb-6 grid gap-3 rounded-lg border border-stone-200 bg-stone-50 p-4 sm:grid-cols-3">
        <div className="text-sm text-stone-700">
          <div className="text-xs uppercase tracking-wide text-stone-500">Assets requiring valuation</div>
          <div className="text-lg font-semibold text-stone-900">{totalAssets}</div>
        </div>
        <div className="text-sm text-stone-700">
          <div className="text-xs uppercase tracking-wide text-stone-500">Completed row checkboxes</div>
          <div className="text-lg font-semibold text-stone-900">{completedCount}</div>
        </div>
        <div className="text-sm text-stone-700">
          <div className="text-xs uppercase tracking-wide text-stone-500">Total indicated value (personal + DoD cols)</div>
          <div className="text-lg font-semibold text-stone-900">{formatMoney(portfolioTotal)}</div>
        </div>
        {headerNote ? <div className="sm:col-span-3 text-xs text-stone-500">{headerNote}</div> : null}
      </div>

      <section className="mb-8 rounded-xl border border-grey-200 bg-white-alt p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-blue-900">Business interests</h2>
            <p className="text-xs text-grey-600">
              LLCs, partnerships, closely held stock — kept separate from household personal property. Each entity appears in the valuation table below for appraisal tracking.
            </p>
          </div>
          <button
            type="button"
            disabled={isArchived}
            onClick={() => {
              setEditingBiz(emptyBiz());
              setBizModalOpen(true);
            }}
            className="rounded-md bg-cyan-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            Add entity
          </button>
        </div>

        {businessInterests.length === 0 ? (
          <p className="text-sm text-grey-600 py-2">No business interests added yet.</p>
        ) : (
          <ul className="space-y-2">
            {businessInterests.map((b) => (
              <li
                key={b.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm"
              >
                <div>
                  <div className="font-medium text-stone-900">{b.entityName.trim() || 'Unnamed entity'}</div>
                  <div className="text-xs text-stone-500">
                    Ownership {b.ownershipPct || '—'} · {b.role || 'Role not set'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={isArchived}
                    className="text-xs font-semibold text-cyan-700 hover:underline disabled:opacity-50"
                    onClick={() => {
                      setEditingBiz({ ...b });
                      setBizModalOpen(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    disabled={isArchived}
                    className="text-xs font-semibold text-red-700 hover:underline disabled:opacity-50"
                    onClick={() => setBusinessInterests((rows) => rows.filter((x) => x.id !== b.id))}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-grey-200 bg-white-alt p-4">
        <h2 className="text-lg font-semibold text-blue-900 mb-1">Valuation &amp; appraisals</h2>
        <p className="text-xs text-grey-600 mb-4">
          Rows are seeded from real estate and vehicles (sframe-155), financial accounts from the planner (frames 186–189 via read-only planner data — aligned with assets reviewed on sframe-144/sframe-146), and business interests you add above.
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                <th className="py-2 pr-2">Asset type</th>
                <th className="py-2 pr-2">Name / description</th>
                <th className="py-2 pr-2">Valuation status</th>
                <th className="py-2 pr-2">Professional engaged</th>
                <th className="py-2 pr-2">Date-of-death value</th>
                <th className="py-2 pr-2">Completion</th>
                <th className="py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {valuationTable.map((item) => {
                const open = expandedRowId === item.id;
                const pills = valuationBadges(item);
                return (
                  <Fragment key={item.id}>
                    <tr className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-2 text-stone-800 align-top">{item.assetType}</td>
                      <td className="py-3 px-2 text-stone-800 align-top">{item.name}</td>
                      <td className="py-3 px-2 align-top">
                        <select
                          value={item.status}
                          disabled={isArchived}
                          onChange={(e) =>
                            patchRow(item.id, { status: e.target.value as ValuationTableRow['status'] })
                          }
                          className="text-sm border border-stone-200 rounded-md px-2 py-1 bg-white text-stone-700 w-full max-w-[11rem]"
                        >
                          {VAL_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {pills.map((p) => (
                            <span key={p.key} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.className}`}>
                              {p.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-2 align-top text-stone-700">
                        {item.status === 'Professional Engaged' || item.professional.trim() ? (
                          <span className="font-medium">{item.professional.trim() || 'Professional engaged (add name)'}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-3 px-2 align-top">
                        <input
                          className="w-28 rounded-md border border-stone-200 px-2 py-1 text-xs"
                          value={item.dodValue}
                          disabled={isArchived}
                          placeholder="$"
                          onChange={(e) => patchRow(item.id, { dodValue: e.target.value })}
                        />
                      </td>
                      <td className="py-3 px-2 align-top">
                        <input
                          type="checkbox"
                          checked={item.complete}
                          disabled={isArchived}
                          aria-label={`Mark valuation complete for ${item.name}`}
                          onChange={(e) => patchRow(item.id, { complete: e.target.checked })}
                        />
                      </td>
                      <td className="py-3 px-2 align-top">
                        <button
                          type="button"
                          onClick={() => setExpandedRowId(open ? null : item.id)}
                          className="text-stone-400 hover:text-stone-600"
                          aria-expanded={open}
                          aria-label={open ? 'Collapse row' : 'Expand row'}
                        >
                          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                    </tr>
                    {open ? (
                      <tr className="border-b border-stone-100 bg-stone-50/80">
                        <td colSpan={7} className="py-4 px-4">
                          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <label className="block text-xs font-medium text-stone-700">
                              Valuation type
                              <select
                                value={item.valuationType}
                                disabled={isArchived}
                                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 bg-white text-stone-700"
                                onChange={(e) =>
                                  patchRow(item.id, { valuationType: e.target.value as ValuationTableRow['valuationType'] })
                                }
                              >
                                <option value="">Select…</option>
                                {VALUATION_TYPES_NO_EMPTY.map((v) => (
                                  <option key={v} value={v}>
                                    {v}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="block text-xs font-medium text-stone-700 sm:col-span-2 lg:col-span-1">
                              Appraiser / professional name (optional)
                              <input
                                className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5"
                                disabled={isArchived}
                                value={item.professional}
                                onChange={(e) => patchRow(item.id, { professional: e.target.value })}
                              />
                            </label>
                            <label className="block text-xs font-medium text-stone-700">
                              Report upload
                              <input
                                type="file"
                                disabled={isArchived}
                                className="mt-1 block w-full text-xs text-stone-600"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) patchRow(item.id, { reportFileName: f.name, reportFileSize: f.size });
                                }}
                              />
                              {item.reportFileName ? (
                                <span className="mt-1 block text-[11px] text-stone-500">
                                  Attached: {item.reportFileName}
                                  {item.reportFileSize ? ` (${Math.round(item.reportFileSize / 1024)} KB)` : ''}
                                </span>
                              ) : null}
                            </label>
                            <label className="flex items-center gap-2 text-xs font-medium text-stone-700 mt-6">
                              <input
                                type="checkbox"
                                checked={item.dodConfirmed}
                                disabled={isArchived}
                                onChange={(e) => patchRow(item.id, { dodConfirmed: e.target.checked })}
                              />
                              Date-of-death value confirmed (teal badge)
                            </label>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {valuationTable.length === 0 ? (
          <p className="text-sm text-stone-500 py-4">No valuation rows yet. Add assets in prior steps or create a business interest above.</p>
        ) : null}

        <p className="mt-4 text-xs text-stone-500">
          Mark each row complete when valuation is finished. Check off every row before treating this step as done.
        </p>
      </section>

      <Modal
        open={bizModalOpen}
        title={
          editingBiz && businessInterests.some((b) => b.id === editingBiz.id) ? 'Edit business interest' : 'Add business interest'
        }
        onCancel={() => {
          setBizModalOpen(false);
          setEditingBiz(null);
        }}
        onOk={saveBizModal}
        okText="Save"
        containerClassName="max-w-lg"
      >
        {editingBiz ? (
          <div className="space-y-3 text-sm">
            <label className="block text-stone-700">
              Legal entity name
              <input
                className="mt-1 w-full rounded-md border border-grey-300 px-2 py-1.5"
                value={editingBiz.entityName}
                disabled={isArchived}
                onChange={(e) => setEditingBiz({ ...editingBiz, entityName: e.target.value })}
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-2">
              <label className="block text-stone-700">
                Ownership %
                <input
                  className="mt-1 w-full rounded-md border border-grey-300 px-2 py-1.5"
                  value={editingBiz.ownershipPct}
                  disabled={isArchived}
                  onChange={(e) => setEditingBiz({ ...editingBiz, ownershipPct: e.target.value })}
                />
              </label>
              <label className="block text-stone-700">
                Management role
                <input
                  className="mt-1 w-full rounded-md border border-grey-300 px-2 py-1.5"
                  value={editingBiz.role}
                  disabled={isArchived}
                  onChange={(e) => setEditingBiz({ ...editingBiz, role: e.target.value })}
                />
              </label>
              <label className="block text-stone-700 sm:col-span-2">
                CPA / attorney contact
                <input
                  className="mt-1 w-full rounded-md border border-grey-300 px-2 py-1.5"
                  value={editingBiz.advisorContact}
                  disabled={isArchived}
                  onChange={(e) => setEditingBiz({ ...editingBiz, advisorContact: e.target.value })}
                />
              </label>
              <label className="block text-stone-700 sm:col-span-2">
                Transfer / buy-sell notes
                <textarea
                  className="mt-1 w-full min-h-[56px] rounded-md border border-grey-300 px-2 py-1.5 text-sm"
                  value={editingBiz.notes}
                  disabled={isArchived}
                  onChange={(e) => setEditingBiz({ ...editingBiz, notes: e.target.value })}
                />
              </label>
            </div>
          </div>
        ) : null}
      </Modal>
    </SettlementStepPage>
  );
}
